import {
  allocateSnapshotQuantity,
  getMissingQuantityForContext,
  normalizeCartStockSnapshot,
} from "@/helper/warehouseNeedCart";
import {
  getDefaultSupplierOrderUnit,
  normalizeDepositEstimate,
} from "@/helper/supplierOrderCart";

export default {
  methods: {
    getWarehouseNeedsCartEndpoint() {
      const url = this.$backend
        .get("WAREHOUSE_PRODUCT", "ANALYSIS_CART_PREVIEW", this.selectedFirm.id)
        .replace("{firm_id}", this.selectedFirm.id);
      return new URL(url);
    },

    getWarehouseNeedsCartParams() {
      return {};
    },

    appendWarehouseNeedsCartParams(url, params) {
      Object.keys(params || {}).forEach((key) => {
        const value = params[key];
        if (value !== null && typeof value !== "undefined" && value !== "") {
          url.searchParams.append(key, value);
        }
      });
    },

    async fetchWarehouseNeedsCartPreview() {
      const url = this.getWarehouseNeedsCartEndpoint();
      this.appendWarehouseNeedsCartParams(url, this.getWarehouseNeedsCartParams());
      const { data } = await this.$axios.get(url);
      return data;
    },

    getWarehouseNeedsProductCartItems(product) {
      if (typeof this.getProductCartItems === "function") {
        return this.getProductCartItems(product);
      }

      if (typeof this.productCartItems === "function") {
        return this.productCartItems(product);
      }

      return [];
    },

    getMissingCartQuantity(product) {
      const context = normalizeCartStockSnapshot(
        product?.need_context || product?.stockSnapshot || {}
      );
      return Math.max(
        0,
        Math.ceil(
          getMissingQuantityForContext(
            context,
            this.getWarehouseNeedsProductCartItems(product)
          )
        )
      );
    },

    getWarehouseNeedsScopeWarehouseIds(product) {
      const fallbackWarehouseIds = this.selectedWarehouse
        ? [Number(this.selectedWarehouse.id)]
        : [];
      const contextScope = product?.need_context?.scope_warehouse_ids || [];
      return (contextScope.length
        ? contextScope
        : product.scope_warehouse_ids || fallbackWarehouseIds
      ).map(Number);
    },

    getWarehouseNeedsSupplierPrice(supplier) {
      const orderUnitPrice = this.getWarehouseNeedsSupplierOrderUnit(supplier)?.price;
      if (orderUnitPrice) {
        return orderUnitPrice;
      }

      if (typeof this.getSupplierPrice === "function") {
        return this.getSupplierPrice(supplier);
      }

      return supplier?.offer_price || supplier?.unit_price || supplier?.product_sale_price?.sale_price || {
        amount: 0,
        currency: "EUR",
      };
    },

    getWarehouseNeedsSupplierOrderUnit(supplier) {
      return getDefaultSupplierOrderUnit(supplier);
    },

    getWarehouseNeedsPackagedQuantity(product) {
      if (product?.packaging?.packaged_product_quantity) {
        return product.packaging.packaged_product_quantity;
      }

      if (typeof this.getPackagedQty === "function") {
        return this.getPackagedQty(product);
      }

      return null;
    },

    getWarehouseNeedsCartProductSuppliers(product) {
      if (typeof this.getCartProductSuppliers === "function") {
        return this.getCartProductSuppliers(product);
      }

      const suppliers = product?.suppliers?.length
        ? product.suppliers
        : product?.supplied_firms || [];

      return suppliers.map((item) => ({
        id: item.id,
        name: item.name,
        logo_url: item.logo_path || item.logo_url,
        unitPrice: this.getWarehouseNeedsSupplierPrice(item),
        minimum_order_amount: item.minimum_order_amount,
        supplier_product_id: item.supplier_product_id,
        supplier_external_product_id: item.supplier_external_product_id,
      }));
    },

    buildWarehouseNeedsCartItem(product, supplier, quantity) {
      const scopeWarehouseIds = this.getWarehouseNeedsScopeWarehouseIds(product);
      const orderUnit = this.getWarehouseNeedsSupplierOrderUnit(supplier);
      const price = this.getWarehouseNeedsSupplierPrice(supplier);
      const stockSnapshot = allocateSnapshotQuantity(
        normalizeCartStockSnapshot({
          ...(product.need_context || {}),
          scopeWarehouseIds,
        }),
        quantity
      );

      return {
        id: `${product.id}-${supplier.id}-${orderUnit?.id || "default"}-`,
        product: {
          id: product.id,
          name: product.name,
          gtin: product.gtin,
          image_url: product.cover_image_url,
          department_id: product.department_id || 1,
          packaged_qty: this.getWarehouseNeedsPackagedQuantity(product),
        },
        supplierId: supplier.id,
        supplierProductId: supplier.supplier_product_id,
        supplierExternalProductId: supplier.supplier_external_product_id,
        supplier: {
          id: supplier.id,
          name: supplier.name,
          logo_url: supplier.logo_path || supplier.logo_url,
          minimum_order_amount: supplier.minimum_order_amount,
          supplier_product_id: supplier.supplier_product_id,
          supplier_external_product_id: supplier.supplier_external_product_id,
        },
        price: {
          id: price.id || null,
          amount: parseFloat(price.amount) || 0,
          currency: price.currency || "EUR",
          tax: parseFloat(price.tax) || 0,
        },
        orderUnitId: orderUnit?.id || null,
        orderUnit,
        depositEstimate: normalizeDepositEstimate(orderUnit?.deposit_estimate),
        quantity,
        reason: "",
        scopeWarehouseIds,
        stockSnapshot,
        productSuppliers: this.getWarehouseNeedsCartProductSuppliers(product),
      };
    },

    buildWarehouseNeedsCartCandidates(data) {
      return (data.data || [])
        .map((entry) => {
          const quantity = this.getMissingCartQuantity(entry.product);
          return entry.addable && quantity > 0
            ? this.buildWarehouseNeedsCartItem(entry.product, entry.selected_supplier, quantity)
            : null;
        })
        .filter(Boolean);
    },

    getWarehouseNeedsCartCandidates(data) {
      return this.buildWarehouseNeedsCartCandidates(data);
    },

    getWarehouseNeedsCartSummary(data, candidates) {
      const skipped = (data.summary?.skipped_products_count || 0) +
        (data.summary?.addable_products_count || 0) - candidates.length;
      const totalQuantity = candidates.reduce((sum, item) => sum + item.quantity, 0);
      const existingProductIds = new Set(
        this.cartItems
          .filter((item) => (parseFloat(item.quantity) || 0) > 0)
          .map((item) => Number(item.product?.id))
      );
      const completed = candidates.filter((item) =>
        existingProductIds.has(Number(item.product.id))
      ).length;

      return {
        added: candidates.length - completed,
        completed,
        skipped,
        totalQuantity,
      };
    },

    async addAllNeedsToCart() {
      this.bulkLoading = true;
      try {
        const data = await this.fetchWarehouseNeedsCartPreview();
        const candidates = this.getWarehouseNeedsCartCandidates(data);
        const summary = this.getWarehouseNeedsCartSummary(data, candidates);

        if (!candidates.length) {
          this.$toast.info(this.$t("warehouse.no_cart_needs"));
          return;
        }

        this.$confirm({
          title: this.$t("warehouse.add_all_needs_to_cart"),
          message: this.$t("warehouse.bulk_cart_confirm", {
            count: candidates.length,
            quantity: summary.totalQuantity,
            skipped: summary.skipped,
          }),
          button: { no: this.$t("global.no"), yes: this.$t("global.yes") },
          callback: async (confirm) => {
            if (!confirm) return;
            await this.$store.dispatch("cart/bulkAddToCart", candidates);
            this.$toast.success(this.$t("warehouse.bulk_cart_added", {
              added: summary.added,
              completed: summary.completed,
              quantity: summary.totalQuantity,
              skipped: summary.skipped,
            }));
            if (typeof this.afterWarehouseNeedsCartAdded === "function") {
              await this.afterWarehouseNeedsCartAdded(candidates, summary);
            }
          },
        });
      } catch (error) {
        this.$toast.error(error.response?.data?.message || this.$t("global.error_occurred"));
      } finally {
        this.bulkLoading = false;
      }
    },
  },
};
